import { SimplifiedOpportunityDetailsHeader, useReferrer } from "@skv/core";
import { opportunityStore } from "@skv/state";
import * as React from "react";
import {
  LinkProps,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { Error500 } from "~/ui";
import {
  ErrorEoiApplied,
  ErrorEoiLimitReached,
  OpportunityDetails404,
} from "~/ui/ServerResponse";
import OpportunityDetailsAside from "./OpportunityDetailsAside";
import { OpportunityDetailsEoi } from "./OpportunityDetailsEoi";
import OpportunityDetailsEoiSuccess from "./OpportunityDetailsEoiSuccess";
import { OpportunityDetailsHeader } from "./OpportunityDetailsHeader";
import OpportunityDetailsMain from "./OpportunityDetailsMain";
import OpportunityDetailsEoiRedirect from "./OpportunityDetailsEoiRedirect";

const OpportunityDetails = (): JSX.Element => {
  const match = useRouteMatch();
  const params = useParams<{ id?: string }>();
  const referrer = useReferrer();
  const wrappingRef = React.useRef(null);

  const { pathname, state: locationState } = useLocation<{
    referer?: LinkProps<{
      pathname?: string;
    }>["to"];
  }>();

  if (!params.id) {
    return <Redirect to="/volunteering" />;
  }

  const referrerPathname: string =
    locationState?.referer && typeof locationState?.referer === "object"
      ? locationState.referer.pathname
      : locationState?.referer && typeof locationState?.referer === "string"
      ? locationState.referer
      : null;

  const focus =
    referrerPathname === "/apply" ? "applyButton" : "opp-details-heading";

  const {
    loading,
    error,
    data: opportunity,
  } = opportunityStore.hooks.useOpportunity(params.id);

  if (error) {
    switch (error.errorCode) {
      case "error:opportunity:notfound":
        return <OpportunityDetails404 />;
      case "error:application:alreadyexists":
        return <ErrorEoiApplied />;
      case "error:application:limitreached":
        return <ErrorEoiLimitReached />;
      default:
        return <Error500 />;
    }
  }

  return (
    <div ref={wrappingRef} className="my-4">
      {((): React.ReactNode => {
        if (loading) {
          return <p className="mt-3">Loading please wait...</p>;
        }

        if (!opportunity) {
          return <p>Something went wrong</p>;
        }

        if (opportunity) {
          return (
            <article>
              <Switch>
                <Route exact
                  path={[
                    `${match.path}/apply/success`,
                    `${match.path}/apply/:encodedOnlineApplicationId/redirect`,
                  ]}
                >
                  {opportunity.organisation?.twoStepApplication ? (
                    <SimplifiedOpportunityDetailsHeader
                      title="Part one of your two-step application has been sent"
                      imgSrc={opportunity.organisation?.logoUrl}
                      organisationName={opportunity.organisation?.name}
                      isNew={opportunity.isNew}
                      focusHeading={focus === "opp-details-heading"}
                    />
                  ) : (
                    <OpportunityDetailsHeader
                      key={pathname}
                      title={opportunity.name}
                      organisationName={opportunity.organisation?.name}
                      to={{
                        pathname: opportunity.organisation?.url,
                        state: {
                          referrer: {
                            state: {
                              ...locationState,
                              referrer: referrer,
                              children: "Back to opportunity",
                            },
                          },
                        },
                      }}
                      eventDate={opportunity.eventStartDate}
                      imgSrc={opportunity.organisation?.logoUrl}
                      focusHeading={focus === "opp-details-heading"}
                    />
                  )}
                </Route>
                <Route>
                  <OpportunityDetailsHeader
                    key={pathname}
                    title={opportunity.name}
                    organisationName={opportunity.organisation?.name}
                    to={{
                      pathname: opportunity.organisation?.url,
                      state: {
                        referrer: {
                          state: {
                            ...locationState,
                            referrer: referrer,
                            children: "Back to opportunity",
                          },
                        },
                      },
                    }}
                    eventDate={opportunity.eventStartDate}
                    imgSrc={opportunity.organisation?.logoUrl}
                    focusHeading={focus === "opp-details-heading"}
                  />
                </Route>
              </Switch>

              <div className="row">
                {/* Main Content Area */}
                <section className="col-md-7 mb-3">
                  <Switch>
                    {/* Two-step application redirect handler */}
                    <Route
                      path={`${match.path}/apply/redirect/:encodedOnlineApplicationId`}
                    >
                      <OpportunityDetailsEoiRedirect />
                    </Route>

                    {/* Application success page */}
                    <Route path={`${match.path}/apply/success`}>
                      <OpportunityDetailsEoiSuccess />
                    </Route>

                    {/* Application form */}
                    <Route path={`${match.path}/apply`}>
                      <OpportunityDetailsEoi />
                    </Route>

                    {/* Main opportunity details page */}
                    <Route path={match.path}>
                      <OpportunityDetailsMain
                        focusApply={focus === "applyButton"}
                      />
                    </Route>
                  </Switch>
                </section>

                {/* Sidebar Area */}
                <Switch>
                  <Route
                    path={[
                    `${match.path}/apply/success`,
                    `${match.path}/apply/:encodedOnlineApplicationId/redirect`,
                    ]}
                  >
                    {/* Exclude opp sidebar */}
                  </Route>
                  <Route>
                    <OpportunityDetailsAside title={opportunity.name} />
                  </Route>
                </Switch>
              </div>
            </article>
          );
        }

        return <p>Something went wrong</p>;
      })()}
    </div>
  );
};

export default OpportunityDetails;
